import React, { useState, useEffect, useCallback } from 'react';
import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ToggleOn as ActiveIcon,
  ToggleOff as InactiveIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
} from '@material-ui/icons';
import { aiKnowledgeApi } from '../../services/apiAiKnowledge';

const useStyles = makeStyles((theme) => ({
  container: {
    maxWidth: '100%',
    margin: '0 auto',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  filters: {
    backgroundColor: 'white',
    padding: theme.spacing(3),
    borderRadius: theme.spacing(1.5),
    boxShadow:
      '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    marginBottom: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  formControl: {
    minWidth: 180,
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.spacing(1),
      '&:hover fieldset': {
        borderColor: '#3b82f6',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#3b82f6',
        boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
      },
    },
  },
  button: {
    padding: theme.spacing(1, 2),
    borderRadius: theme.spacing(1),
    textTransform: 'none',
    fontWeight: 600,
    '&:hover': {
      transform: 'translateY(-1px)',
    },
  },
  refreshButton: {
    backgroundColor: '#3b82f6',
    color: 'white',
    '&:hover': {
      backgroundColor: '#2563eb',
    },
    '&:disabled': {
      backgroundColor: '#9ca3af',
    },
  },
  table: {
    width: '100%',
    borderRadius: theme.spacing(1.5),
    boxShadow:
      '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    overflow: 'hidden',
  },
  tableHeader: {
    backgroundColor: '#f8fafc',
    '& .MuiTableCell-head': {
      fontWeight: 600,
      color: '#374151',
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    },
  },
  tableRow: {
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#f9fafb',
    },
  },
  tableCell: {
    color: '#6b7280',
    fontSize: '0.875rem',
    borderBottom: '1px solid #f3f4f6',
  },
  keywordCell: {
    fontWeight: 600,
    color: '#1f2937',
  },
  systemChip: {
    backgroundColor: '#f3f4f6',
    color: '#374151',
    fontSize: '0.75rem',
    fontWeight: 500,
    height: 'auto',
    padding: theme.spacing(0.5, 1),
  },
  statusActive: {
    backgroundColor: '#dcfce7',
    color: '#166534',
  },
  statusInactive: {
    backgroundColor: '#fef2f2',
    color: '#991b1b',
  },
  priorityBadge: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  priority1: { backgroundColor: '#10b981' },
  priority2: { backgroundColor: '#84cc16' },
  priority3: { backgroundColor: '#f59e0b' },
  priority4: { backgroundColor: '#f97316' },
  priority5: { backgroundColor: '#ef4444' },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  actionButton: {
    minWidth: 'auto',
    padding: theme.spacing(0.5),
    borderRadius: theme.spacing(0.75),
    '&:hover': {
      transform: 'translateY(-1px)',
    },
  },
  toggleButton: {
    color: '#92400e',
    backgroundColor: '#fef3c7',
    '&:hover': {
      backgroundColor: '#fde68a',
    },
  },
  editButton: {
    color: '#1e40af',
    backgroundColor: '#dbeafe',
    '&:hover': {
      backgroundColor: '#bfdbfe',
    },
  },
  deleteButton: {
    color: '#991b1b',
    backgroundColor: '#fee2e2',
    '&:hover': {
      backgroundColor: '#fecaca',
    },
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(5),
    color: '#6b7280',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(5),
    color: '#6b7280',
    backgroundColor: 'white',
    borderRadius: theme.spacing(1.5),
    boxShadow:
      '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  },
  emptyStateIcon: {
    fontSize: 48,
    color: '#9ca3af',
    marginBottom: theme.spacing(2),
  },
  tagsCell: {
    minWidth: 220,
  },
  tagsWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  tagChip: {
    backgroundColor: '#eef2ff',
    color: '#3730a3',
    fontSize: '0.7rem',
    height: 24,
  },
}));

const AiKnowledgeList = ({ onEdit }) => {
  const [knowledge, setKnowledge] = useState([]);
  const [loading, setLoading] = useState(false);
  const [systems, setSystems] = useState([]);
  const [filters, setFilters] = useState({
    system: '',
    isActive: '',
  });
  const classes = useStyles();

  const loadKnowledge = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.system) params.system = filters.system;
      if (filters.isActive !== '') params.isActive = filters.isActive;

      const response = await aiKnowledgeApi.list(params);
      setKnowledge(response.data.data || response.data);
    } catch (error) {
      console.error('Erro ao carregar conhecimento:', error);
      alert('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [filters.system, filters.isActive]);

  const loadSystems = async () => {
    try {
      const response = await aiKnowledgeApi.getSystems();
      setSystems(response.data);
    } catch (error) {
      console.error('Erro ao carregar sistemas:', error);
    }
  };

  useEffect(() => {
    loadKnowledge();
    loadSystems();
  }, [loadKnowledge]);

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este item?')) return;

    try {
      await aiKnowledgeApi.delete(id);
      alert('Item excluído com sucesso!');
      loadKnowledge();
    } catch (error) {
      alert('Erro ao excluir item');
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await aiKnowledgeApi.toggleActive(id);
      alert(`Item ${!currentStatus ? 'ativado' : 'desativado'} com sucesso!`);
      loadKnowledge();
    } catch (error) {
      alert('Erro ao alterar status');
    }
  };

  const getPriorityStyle = (priority) => {
    const priorityMap = {
      1: classes.priority1,
      2: classes.priority2,
      3: classes.priority3,
      4: classes.priority4,
      5: classes.priority5,
    };
    return priorityMap[priority] || classes.priority1;
  };

  return (
    <div className={classes.container}>
      {/* Filtros */}
      <Paper className={classes.filters}>
        <FormControl
          variant="outlined"
          className={classes.formControl}
          size="small"
        >
          <InputLabel>Sistema</InputLabel>
          <Select
            value={filters.system}
            onChange={(e) => setFilters({ ...filters, system: e.target.value })}
            label="Sistema"
          >
            <MenuItem value="">Todos os sistemas</MenuItem>
            {systems.map((system) => (
              <MenuItem key={system} value={system}>
                {system}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          variant="outlined"
          className={classes.formControl}
          size="small"
        >
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.isActive}
            onChange={(e) =>
              setFilters({ ...filters, isActive: e.target.value })
            }
            label="Status"
          >
            <MenuItem value="">Todos os status</MenuItem>
            <MenuItem value="true">Ativo</MenuItem>
            <MenuItem value="false">Inativo</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="contained"
          onClick={loadKnowledge}
          disabled={loading}
          className={`${classes.button} ${classes.refreshButton}`}
          startIcon={
            loading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <RefreshIcon />
            )
          }
        >
          {loading ? 'Carregando...' : 'Atualizar'}
        </Button>
      </Paper>

      {/* Lista */}
      {loading ? (
        <Box className={classes.loading}>
          <CircularProgress size={20} style={{ marginRight: 8 }} />
          <Typography variant="body2">Carregando conhecimentos...</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} className={classes.table}>
          <Table>
            <TableHead className={classes.tableHeader}>
              <TableRow>
                <TableCell>Palavra-chave</TableCell>
                <TableCell>Sistema</TableCell>
                <TableCell>Pergunta</TableCell>
                <TableCell>Tags</TableCell>
                <TableCell>Prioridade</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {knowledge.map((item) => (
                <TableRow key={item.id} className={classes.tableRow}>
                  <TableCell
                    className={`${classes.tableCell} ${classes.keywordCell}`}
                  >
                    {item.keyword}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Chip
                      label={item.system}
                      size="small"
                      className={classes.systemChip}
                    />
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Tooltip title={item.question}>
                      <Typography
                        variant="body2"
                        noWrap
                        style={{ maxWidth: 200 }}
                      >
                        {item.question.length > 50
                          ? `${item.question.substring(0, 50)}...`
                          : item.question}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    className={`${classes.tableCell} ${classes.tagsCell}`}
                  >
                    <div className={classes.tagsWrap}>
                      {(Array.isArray(item.tags) ? item.tags : [])
                        .slice(0, 4)
                        .map((tag) => (
                          <Chip
                            key={`${item.id}-${tag}`}
                            label={tag}
                            size="small"
                            className={classes.tagChip}
                          />
                        ))}
                      {Array.isArray(item.tags) && item.tags.length > 4 && (
                        <Chip
                          label={`+${item.tags.length - 4}`}
                          size="small"
                          className={classes.tagChip}
                        />
                      )}
                    </div>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <div
                      className={`${classes.priorityBadge} ${getPriorityStyle(item.priority)}`}
                    >
                      {item.priority}
                    </div>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Chip
                      icon={item.isActive ? <CheckIcon /> : <CancelIcon />}
                      label={item.isActive ? 'Ativo' : 'Inativo'}
                      size="small"
                      className={
                        item.isActive
                          ? classes.statusActive
                          : classes.statusInactive
                      }
                    />
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <div className={classes.actions}>
                      <Tooltip title={item.isActive ? 'Desativar' : 'Ativar'}>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleToggleActive(item.id, item.isActive)
                          }
                          className={`${classes.actionButton} ${classes.toggleButton}`}
                        >
                          {item.isActive ? <ActiveIcon /> : <InactiveIcon />}
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => onEdit(item)}
                          className={`${classes.actionButton} ${classes.editButton}`}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(item.id)}
                          className={`${classes.actionButton} ${classes.deleteButton}`}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {knowledge.length === 0 && !loading && (
        <Paper className={classes.emptyState}>
          <CancelIcon className={classes.emptyStateIcon} />
          <Typography
            variant="h6"
            gutterBottom
            style={{ color: '#374151', fontWeight: 600 }}
          >
            Nenhum item encontrado
          </Typography>
          <Typography variant="body2">
            {filters.system || filters.isActive !== ''
              ? 'Tente ajustar os filtros para ver mais resultados'
              : 'Comece adicionando um novo conhecimento à base'}
          </Typography>
        </Paper>
      )}
    </div>
  );
};

export default AiKnowledgeList;
